"use client";
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  CircularProgress,
  Alert,
  AlertIcon,
  Grid,
  Button,
  Flex,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spacer,
  IconButton,
  useColorModeValue,
  Spinner
} from '@chakra-ui/react';
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon, CloseIcon } from '@chakra-ui/icons';
import { useClients } from '../hooks/useClients';
import { useDebounce } from '../hooks/useDebounce';
import { ClientCard } from '../components/clients/ClientCard';
import { useMeetingContext } from '../components/meeting/MeetingContext';
import { clientService } from '../services/clientService';

const ClientsPage: React.FC = () => {
  const [filters, setFilters] = useState({
    type: '',
    projectId: '',
    page: 1,
    limit: 12,
    sortBy: 'name',
    sortOrder: 'asc' as 'asc' | 'desc',
    search: ''
  });

  // Debounce search term to avoid too many API calls
  const debouncedSearch = useDebounce(filters.search, 500);

  // Reset page to 1 when search term changes
  useEffect(() => {
    if (debouncedSearch !== filters.search && filters.page !== 1) {
      setFilters(prev => ({ ...prev, page: 1 }));
    }
  }, [debouncedSearch]);

  const { clients, loading, error, pagination, refetch } = useClients({
    type: filters.type || undefined,
    projectId: filters.projectId || undefined,
    page: filters.page,
    limit: filters.limit,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    search: debouncedSearch || undefined
  });

  // Color mode values
  const paginationBg = useColorModeValue("white", "gray.800");
  const buttonHoverBg = useColorModeValue("gray.50", "gray.700");

    const { 
      meetings: contextMeetings,  
    } = useMeetingContext();

  // Filter clients to only show those with request_id
  const clientsWithRequestId = clients.filter(client => 
    client.request_id && client.request_id.trim() !== ''
  );

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value // Reset to page 1 when other filters change
    }));
  };

  const handlePageChange = (newPage: number) => {
    if (pagination && newPage >= 1 && newPage <= pagination.totalPages && newPage !== filters.page) {
      setFilters(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setFilters(prev => ({ 
      ...prev, 
      limit: newLimit, 
      page: 1 // Reset to first page when changing limit
    }));
  };

  // Generate page numbers for pagination
  const generatePageNumbers = () => {
    if (!pagination) return [];
    
    const pages = [];
    const maxPagesToShow = 5;
    const halfRange = Math.floor(maxPagesToShow / 2);
    
    let startPage = Math.max(1, filters.page - halfRange);
    let endPage = Math.min(pagination.totalPages, filters.page + halfRange);
    
    // Adjust if we're near the beginning or end
    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1) {
        endPage = Math.min(pagination.totalPages, startPage + maxPagesToShow - 1);
      } else if (endPage === pagination.totalPages) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  // Enhanced Pagination Controls Component
  const PaginationControls = () => {
    if (!pagination) return null;

    return (
      <Box 
        bg={paginationBg} 
        p={4} 
        borderRadius="md" 
        borderWidth="1px" 
        borderColor="gray.200"
        boxShadow="sm"
      >
        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
          {/* Items per page selector */}
          <HStack spacing={2}>
            <Text fontSize="sm" color="gray.600">Show:</Text>
            <Select 
              size="sm" 
              value={filters.limit} 
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              width="80px"
            >
              <option value={6}>6</option>
              <option value={12}>12</option>
              <option value={24}>24</option>
              <option value={48}>48</option>
            </Select>
            <Text fontSize="sm" color="gray.600">per page</Text>
          </HStack>

          {/* Page info */}
          <Text fontSize="sm" color="gray.600">
            Showing {((filters.page - 1) * filters.limit) + 1} to{" "}
            {Math.min(filters.page * filters.limit, pagination.totalItems)} of{" "}
            {pagination.totalItems} clients (filtered to show only those with request data)
          </Text>

          {/* Page navigation */}
          <HStack spacing={1}>
            <IconButton
              aria-label="Previous page"
              icon={<ChevronLeftIcon />}
              size="sm"
              onClick={() => handlePageChange(filters.page - 1)}
              isDisabled={filters.page === 1}
              _hover={{ bg: buttonHoverBg }}
            />
            
            {generatePageNumbers().map((pageNum) => (
              <Button
                key={pageNum}
                size="sm"
                variant={pageNum === filters.page ? "solid" : "ghost"}
                colorScheme={pageNum === filters.page ? "purple" : "gray"}
                onClick={() => handlePageChange(pageNum)}
                _hover={{ bg: buttonHoverBg }}
              >
                {pageNum}
              </Button>
            ))}
            
            <IconButton
              aria-label="Next page"
              icon={<ChevronRightIcon />}
              size="sm"
              onClick={() => handlePageChange(filters.page + 1)}
              isDisabled={filters.page === pagination.totalPages}
              _hover={{ bg: buttonHoverBg }}
            />
          </HStack>
        </Flex>
      </Box>
    );
  };

  if (loading && clients.length === 0) {
    return (
      <Flex justify="center" align="center" height="100vh">
        <CircularProgress isIndeterminate color="purple.500" />
        <Text ml={4}>Loading clients...</Text>
      </Flex>
    );
  }

  if (error && clients.length === 0) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          Error loading clients: {error}
          <Button ml={4} onClick={refetch} size="sm">
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Text fontSize="3xl" fontWeight="bold" color="gray.800" mb={2}>
            Clients
          </Text>
          <Text color="gray.600">
            Manage and view all client information
          </Text>
        </Box>

        {/* Filters */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <VStack spacing={4}>
            <HStack spacing={4} width="100%">
              <InputGroup flex={1}>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search clients by name or email..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
                {filters.search && filters.search !== debouncedSearch ? (
                  <InputRightElement>
                    <CircularProgress size="20px" isIndeterminate />
                  </InputRightElement>
                ) : filters.search ? (
                  <InputRightElement>
                    <IconButton
                      aria-label="Clear search"
                      icon={<CloseIcon />}
                      size="xs"
                      variant="ghost"
                      onClick={() => handleFilterChange('search', '')}
                    />
                  </InputRightElement>
                ) : null}
              </InputGroup>
              <Select
                placeholder="Client Type"
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                width="200px"
              >
                <option value="real_estate">Real Estate</option>
                <option value="sales">Sales</option>
              </Select>
              <Select
                placeholder="Sort By"
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                width="150px"
              >
                <option value="name">Name</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
              </Select>
              <Select
                value={filters.sortOrder}
                onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                width="100px"
              >
                <option value="asc">A-Z</option>
                <option value="desc">Z-A</option>
              </Select>
            </HStack>
          </VStack>
        </Box>

        {/* Results */}
        {clientsWithRequestId.length === 0 ? (
          <Box textAlign="center" py={12}>
            <Text fontSize="lg" color="gray.500">
              {clients.length === 0 ? "No clients found" : "No clients with request data found"}
            </Text>
            {clients.length > 0 && clientsWithRequestId.length === 0 && (
              <Text fontSize="sm" color="gray.400" mt={2}>
                Showing only clients with valid request IDs ({clientsWithRequestId.length} of {clients.length} clients)
              </Text>
            )}
          </Box>
        ) : (
          <>
            <Box mb={4}>
              <Text fontSize="sm" color="gray.600">
                Showing {clientsWithRequestId.length} of {clients.length} clients with request data
                {debouncedSearch && (
                  <Text as="span" color="purple.600" ml={2}>
                    (filtered by: "{debouncedSearch}")
                  </Text>
                )}
              </Text>
            </Box>
            
          {loading && clients.length > 0 && (
            <Flex justify="center" py={4}>
              <CircularProgress isIndeterminate size="50px" />
              <Text ml={2} fontSize="sm">Updating...</Text>
            </Flex>
          )}

            <Grid
              templateColumns="repeat(auto-fill, minmax(550px, 1fr))"
              gap={6}
            >
              {clientsWithRequestId.map((client) => (
                <ClientCard key={client.id} client={client} clientType={filters.type || undefined} />
              ))}
            </Grid>
          </>
        )}

        {/* Bottom Pagination Controls */}
        {pagination && pagination.totalPages > 1 && (
          <PaginationControls />
        )}

      </VStack>
    </Container>
  );
};

export default ClientsPage; 